/**
 * 動画の「次の動画」プレビューを出すタイミングの計算
 *
 * 学習前は動画の長さに応じた残り秒数で出し、
 * 学習後は作品ごとに「次へ」を押した残り秒数（中央値）の少し手前で出す。
 * 表示は2段階で、早い段階は控えめなチップ、遅い段階はサムネイル付きカードにする。
 */

export type NextPreviewStage = 'chip' | 'card'

export type NextPreviewThresholds = {
  /** 残りこの秒数を切ったらチップを出す */
  chipSec: number
  /** 残りこの秒数を切ったらカードを出す */
  cardSec: number
}

const DEFAULT_CARD_RATIO = 0.05
const DEFAULT_CARD_MIN_SEC = 15
const DEFAULT_CARD_MAX_SEC = 90
/** 学習値より手前に出す余裕 */
const LEARNED_MARGIN_SEC = 10
const LEARNED_CARD_MIN_SEC = 10
const LEARNED_CARD_MAX_SEC = 180
/** カードよりチップを早く出す秒数 */
const CHIP_LEAD_SEC = 30

/** 学習に使う残り秒数の範囲（バックエンドの検証と揃える） */
export const MIN_SAMPLE_SEC = 5
export const MAX_SAMPLE_SEC = 900
/** 残り割合がこれ以上の位置で押した「次へ」は、スキップや見送りとみなして学習しない */
const SKIP_RATIO = 0.3
/** カードを閉じたときは、表示が早すぎたとみなして残り秒数をこの倍率に縮めて学習する */
const DISMISS_SHRINK = 0.7

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

export const computeNextPreviewThresholds = (
  duration: number,
  learnedRemainingSec: number | null,
): NextPreviewThresholds => {
  if (duration <= 0) return { chipSec: 0, cardSec: 0 }

  const cardSec =
    learnedRemainingSec === null
      ? clamp(duration * DEFAULT_CARD_RATIO, DEFAULT_CARD_MIN_SEC, DEFAULT_CARD_MAX_SEC)
      : clamp(learnedRemainingSec + LEARNED_MARGIN_SEC, LEARNED_CARD_MIN_SEC, LEARNED_CARD_MAX_SEC)

  // 短い動画で再生開始直後から出ないよう、動画の半分までに抑える
  const limit = duration / 2
  return { chipSec: Math.min(cardSec + CHIP_LEAD_SEC, limit), cardSec: Math.min(cardSec, limit) }
}

export const resolveNextPreviewStage = (
  remainingSec: number,
  thresholds: NextPreviewThresholds,
): NextPreviewStage | null => {
  if (remainingSec <= 0) return null
  if (remainingSec < thresholds.cardSec) return 'card'
  if (remainingSec < thresholds.chipSec) return 'chip'
  return null
}

const toValidSample = (seconds: number): number | null => {
  const sample = Math.round(seconds)
  return sample >= MIN_SAMPLE_SEC && sample <= MAX_SAMPLE_SEC ? sample : null
}

/** 「次へ」を押した位置から学習する残り秒数。エンディング付近でなければ null */
export const sampleFromNextPress = (remainingSec: number, duration: number, previewVisible: boolean): number | null => {
  if (duration <= 0) return null
  if (!previewVisible && remainingSec / duration >= SKIP_RATIO) return null
  return toValidSample(remainingSec)
}

/** カードを閉じた位置から学習する残り秒数 */
export const sampleFromDismiss = (remainingSec: number): number | null => toValidSample(remainingSec * DISMISS_SHRINK)
